import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  CheckCircle2,
  ChevronRight,
  Clock3,
  Loader2,
  Printer,
  Receipt,
  RefreshCw,
  Search,
  UserRound,
  X,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import ReceiptDialog from '../../components/ReceiptDialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '../../components/ui/drawer';
import { getOrder, getOrders, getTodayCashRegister } from '../../lib/api';
import {
  extractStornoReason,
  getOrderDisplayName,
  getOrderFulfillmentMeta,
  getOrderSourceMeta,
  isOrderPaid,
} from '../orderMeta';
import { getCashRegisterTimeRange, getCashRegisterUiState } from '../registerState';
import { getPaymentMethodLabel } from '../../lib/paymentMethods';

const FILTERS = [
  { value: 'all', label: 'Alle' },
  { value: 'completed', label: 'Bezahlt' },
  { value: 'cancelled', label: 'Storno' },
];

function StatusPill({ status }) {
  const map = {
    completed: { label: 'Bezahlt', cls: 'bg-lime-100 text-lime-800' },
    cancelled: { label: 'Storno', cls: 'bg-red-100 text-red-700' },
  };
  const current = map[status] || map.completed;
  return (
    <span className={`inline-flex px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] ${current.cls}`}>
      {current.label}
    </span>
  );
}

function MetaBadge({ className, label }) {
  return (
    <span className={`inline-flex px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] ${className}`}>
      {label}
    </span>
  );
}

function SummaryCard({ label, value, icon: Icon, tone }) {
  return (
    <div className={`border px-3 py-3 ${tone}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em]">{label}</p>
        <Icon className="h-4 w-4" />
      </div>
      <p className="mt-2 text-2xl font-black tracking-tight">{value}</p>
    </div>
  );
}

function formatFullDate(value) {
  if (!value) return '–';
  return format(new Date(value), "dd.MM.yyyy 'um' HH:mm", { locale: de });
}

function formatListDate(value) {
  if (!value) return { date: '–', time: '' };
  const date = new Date(value);
  return {
    date: format(date, 'dd.MM.yyyy', { locale: de }),
    time: format(date, 'HH:mm', { locale: de }),
  };
}

function formatCurrency(value) {
  return `€${Number(value || 0).toFixed(2)}`;
}

function getRelevantInvoiceTimestamp(order) {
  return order?.completed_at || order?.cancelled_at || order?.created_at || '';
}

function getVisibleInvoiceCount(order) {
  return (order?.items || []).filter((item) => !item?.voided).length;
}

function getInvoiceNumber(order) {
  return order?.receipt_number || order?.order_number || order?.id;
}

function getPaymentLabel(order, sourceMeta) {
  if (order?.status === 'cancelled') return 'Storno';
  if (order?.payment_method) return getPaymentMethodLabel(order.payment_method);
  if (order?.payment_status) return `Online · ${order.payment_status}`;
  if (sourceMeta?.label && sourceMeta.label !== 'POS') return `${sourceMeta.label} (Online)`;
  return 'Bezahlt';
}

function InvoiceDetail({ order, onReceipt, onStornoInfo, isMobile = false, onClose }) {
  if (!order) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-card">
        <div className={`border-b border-border bg-muted ${isMobile ? 'px-4 pb-4 pt-3' : 'px-5 py-5'}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Rechnungsdetails</p>
              <p className={`mt-1 font-bold text-foreground ${isMobile ? 'text-xl' : 'text-2xl'}`}>Keine Rechnung ausgewählt</p>
              <p className="mt-2 text-sm text-muted-foreground">Wählen Sie eine Rechnung aus der Liste, um die Details zu sehen.</p>
            </div>
            {isMobile ? (
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:bg-muted"
                aria-label="Details schließen"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center p-8 text-center text-muted-foreground">
          <div>
            <Receipt className="mx-auto mb-4 h-20 w-20 opacity-20" />
            <p className="text-xl font-bold text-slate-300">Keine Rechnung ausgewählt.</p>
          </div>
        </div>
      </div>
    );
  }

  const sourceMeta = getOrderSourceMeta(order.order_source);
  const fulfillmentMeta = getOrderFulfillmentMeta(order);
  const stornoReason = extractStornoReason(order.notes);
  const displayName = getOrderDisplayName(order);
  const paymentLabel = getPaymentLabel(order, sourceMeta);

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className={`border-b border-border bg-muted ${isMobile ? 'px-4 pb-4 pt-3' : 'px-5 py-5'}`}>
        <div className={`flex gap-4 ${isMobile ? 'flex-col' : 'items-start justify-between'}`}>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Rechnungsdetails</p>
                <p className={`mt-1 truncate font-bold tracking-tight text-foreground ${isMobile ? 'text-xl' : 'text-2xl'}`}>{displayName}</p>
                <p className="mt-1 text-sm font-medium text-muted-foreground">#{getInvoiceNumber(order)}</p>
              </div>
              {isMobile ? (
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:bg-muted"
                  aria-label="Details schließen"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onReceipt(order.id, order)}
                  className="inline-flex h-11 flex-shrink-0 items-center justify-center border border-border bg-card px-4 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted"
                >
                  <Printer className="mr-2 h-4 w-4" />
                  Drucken
                </button>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <StatusPill status={order.status} />
              <MetaBadge className={sourceMeta.className} label={sourceMeta.label} />
              <MetaBadge className={fulfillmentMeta.className} label={fulfillmentMeta.label} />
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="border border-border bg-card px-3 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Gesamt</p>
            <p className={`mt-2 font-black tracking-tight text-foreground ${isMobile ? 'text-lg' : 'text-2xl'}`}>
              {formatCurrency(order.total)}
            </p>
          </div>
          <div className="border border-border bg-card px-3 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Zahlung</p>
            <p className="mt-2 text-sm font-semibold text-muted-foreground">{paymentLabel}</p>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className={`${isMobile ? 'px-4 py-4' : 'px-5 py-4'} border-b border-border`}>
          <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
            <div className="border border-border bg-muted px-4 py-3">
              <div className="flex items-center gap-2">
                <Clock3 className="h-4 w-4 text-muted-foreground" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Zeitpunkte</p>
              </div>
              <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                <p>Angelegt: {formatFullDate(order.created_at)}</p>
                {order.completed_at ? <p>Bezahlt: {formatFullDate(order.completed_at)}</p> : null}
                {order.cancelled_at ? <p>Storniert: {formatFullDate(order.cancelled_at)}</p> : null}
              </div>
            </div>

            <div className="border border-border bg-muted px-4 py-3">
              <div className="flex items-center gap-2">
                <UserRound className="h-4 w-4 text-muted-foreground" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Zuordnung</p>
              </div>
              <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                <p>Kellner: {order.cashier_name || 'Kellner'}</p>
                {order.table_name ? <p>Tisch: {order.table_name}</p> : null}
                {order.customer_name ? <p>Kunde: {order.customer_name}</p> : null}
              </div>
            </div>
          </div>
        </div>

        <div className={`flex items-center justify-between border-b border-border ${isMobile ? 'px-4 py-3' : 'px-5 py-3'}`}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Artikel</p>
          <p className="text-sm font-medium text-muted-foreground">{getVisibleInvoiceCount(order)} Positionen</p>
        </div>

        <div className="divide-y divide-slate-100">
          {(order.items || []).map((item, index) => (
            <div
              key={`${order.id}-${index}`}
              className={isMobile ? 'px-4 py-4' : 'grid grid-cols-[40px_minmax(0,1fr)_auto] gap-4 px-5 py-4'}
            >
              {isMobile ? (
                <div className="grid grid-cols-[34px_minmax(0,1fr)] gap-3">
                  <span className="pt-0.5 text-lg font-black text-slate-300">{item.quantity}x</span>
                  <div className="min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <p className="min-w-0 text-base font-bold leading-tight text-foreground">{item.product_name}</p>
                      <span className="whitespace-nowrap text-base font-bold tabular-nums text-foreground">
                        {formatCurrency(Number(item.price || 0) * Number(item.quantity || 0))}
                      </span>
                    </div>
                    {item.notes ? <p className="mt-1 text-sm text-muted-foreground">{item.notes}</p> : null}
                  </div>
                </div>
              ) : (
                <>
                  <span className="text-2xl font-black text-slate-300">{item.quantity}</span>
                  <div className="min-w-0">
                    <p className="text-xl font-bold text-foreground">{item.product_name}</p>
                    {item.notes ? <p className="mt-1 text-sm text-muted-foreground">{item.notes}</p> : null}
                  </div>
                  <span className="text-xl font-bold tabular-nums text-foreground">
                    {formatCurrency(Number(item.price || 0) * Number(item.quantity || 0))}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>

        <div className={`${isMobile ? 'px-4 py-4' : 'px-5 py-5'} border-t border-border`}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <div className="flex items-center justify-between gap-3">
              <span>USt.</span>
              <span className="tabular-nums">{formatCurrency(order.vat_total)}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span>Zahlung</span>
              <span className="text-right">{paymentLabel}</span>
            </div>
            {order.status === 'cancelled' ? (
              <div className="border border-red-200 bg-red-50 px-4 py-4 text-red-700">
                <p className="text-xs font-semibold uppercase tracking-[0.08em]">Storno</p>
                <p className="mt-2 text-sm">{stornoReason || order.notes || 'Kein Grund hinterlegt.'}</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {isMobile ? (
        <div className="border-t border-border bg-card px-4 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] pt-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={onStornoInfo}
              className="border border-red-200 bg-red-50 px-4 py-3 text-left text-red-700 transition-colors hover:bg-red-100"
            >
              <span className="block text-base font-bold">Stornieren</span>
              <span className="mt-1 block text-[11px] font-semibold uppercase tracking-[0.12em] text-red-500">
                Rechnung stornieren
              </span>
            </button>
            <button
              type="button"
              onClick={() => onReceipt(order.id, order)}
              className="bg-lime-500 px-4 py-3 text-left text-foreground transition-colors hover:bg-lime-600"
            >
              <span className="block text-base font-bold">Drucken</span>
              <span className="mt-1 block text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground/60">
                Belegkopie
              </span>
            </button>
          </div>
        </div>
      ) : (
        <div className="grid flex-shrink-0 grid-cols-2 border-t border-border">
          <button
            type="button"
            onClick={onStornoInfo}
            className="bg-red-600 px-5 py-5 text-left text-white transition-colors hover:bg-red-700"
          >
            <span className="block text-2xl font-bold">Stornieren</span>
            <span className="mt-1 block text-xs font-semibold uppercase tracking-[0.12em] text-white/80">
              Rechnung stornieren
            </span>
          </button>
          <button
            type="button"
            onClick={() => onReceipt(order.id, order)}
            className="bg-lime-500 px-5 py-5 text-right text-foreground transition-colors hover:bg-lime-600"
          >
            <span className="block text-2xl font-bold">Drucken</span>
            <span className="mt-1 block text-xs font-semibold uppercase tracking-[0.12em] text-foreground/60">
              Kopie
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

function InvoiceRow({ order, selected, onSelect, onReceipt }) {
  const sourceMeta = getOrderSourceMeta(order.order_source);
  const displayName = getOrderDisplayName(order);
  const paymentLabel = getPaymentLabel(order, sourceMeta);
  const timeParts = formatListDate(getRelevantInvoiceTimestamp(order));

  return (
    <>
      <button
        type="button"
        onClick={() => onSelect(order)}
        className={`hidden w-full border-b border-border text-left transition-colors lg:block ${
          selected ? 'bg-muted' : 'hover:bg-muted'
        }`}
      >
        <div className="min-h-[78px] items-center gap-4 px-4 lg:grid lg:grid-cols-[88px_110px_120px_minmax(0,1fr)_180px_120px]">
          <div className="text-sm font-medium text-muted-foreground">{getInvoiceNumber(order)}</div>
          <div>
            <p className="text-lg font-bold leading-tight text-foreground">{timeParts.date}</p>
            <p className="mt-1 text-sm text-muted-foreground">{timeParts.time}</p>
          </div>
          <div className="text-lg text-muted-foreground">{order.cashier_name || 'Kellner'}</div>
          <div className="min-w-0">
            <p className="truncate text-xl font-bold text-foreground">{displayName}</p>
            <div className="mt-1 flex flex-wrap gap-1">
              <MetaBadge className={sourceMeta.className} label={sourceMeta.label} />
              <span className="text-xs text-muted-foreground">{getVisibleInvoiceCount(order)} Artikel</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold tabular-nums text-foreground">{formatCurrency(order.total)}</p>
            <p className="mt-1 text-sm text-muted-foreground">{paymentLabel}</p>
          </div>
          <div className="flex justify-end">
            <StatusPill status={order.status} />
          </div>
        </div>
      </button>

      <article
        className={`mx-3 mt-3 overflow-hidden border border-border bg-card shadow-sm transition-shadow last:mb-3 lg:hidden ${
          selected ? 'ring-2 ring-slate-900/10' : ''
        }`}
      >
        <button type="button" onClick={() => onSelect(order)} className="w-full px-4 py-4 text-left">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Rechnung #{getInvoiceNumber(order)}
              </p>
              <p className="mt-1 truncate text-lg font-black tracking-tight text-foreground">{displayName}</p>
            </div>
            <p className="whitespace-nowrap text-lg font-black tabular-nums text-foreground">{formatCurrency(order.total)}</p>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <StatusPill status={order.status} />
            <MetaBadge className={sourceMeta.className} label={sourceMeta.label} />
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-muted-foreground">
            <div className="border border-border bg-muted px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Zeit</p>
              <p className="mt-1 font-semibold text-muted-foreground">{timeParts.date}</p>
              <p className="text-xs text-muted-foreground">{timeParts.time}</p>
            </div>
            <div className="border border-border bg-muted px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Zahlung</p>
              <p className="mt-1 text-sm font-semibold text-muted-foreground">{paymentLabel}</p>
            </div>
          </div>

          <p className="mt-3 text-sm text-muted-foreground">
            {order.cashier_name || 'Kellner'} · {getVisibleInvoiceCount(order)} Artikel
          </p>
        </button>

        <div className="grid grid-cols-2 gap-2 border-t border-border px-4 pb-4 pt-3">
          <button
            type="button"
            onClick={() => onSelect(order)}
            className="inline-flex h-11 items-center justify-center gap-2 border border-border bg-card px-3 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted"
          >
            Details
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onReceipt(order.id, order)}
            className="inline-flex h-11 items-center justify-center gap-2 bg-lime-500 px-3 text-sm font-semibold text-foreground transition-colors hover:bg-lime-600"
          >
            <Printer className="h-4 w-4" />
            Drucken
          </button>
        </div>
      </article>
    </>
  );
}

export default function PosInvoices() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [register, setRegister] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showMobileDetail, setShowMobileDetail] = useState(false);

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const activeRegister = await getTodayCashRegister();
      setRegister(activeRegister);

      const timeRange = getCashRegisterTimeRange(activeRegister);
      if (!timeRange) {
        setOrders([]);
        return;
      }

      const registerDate = String(activeRegister?.date || '').replace(/-/g, '');
      const data = await getOrders(null, registerDate || null);
      const windowStart = new Date(timeRange.openedAt).getTime();
      const windowEnd = timeRange.closedAt ? new Date(timeRange.closedAt).getTime() : Number.POSITIVE_INFINITY;

      const filtered = data
        .filter((order) => order.status === 'cancelled' || isOrderPaid(order))
        .filter((order) => {
          const relevantTime = new Date(getRelevantInvoiceTimestamp(order) || 0).getTime();
          return relevantTime >= windowStart && relevantTime <= windowEnd;
        })
        .sort((left, right) => {
          const leftTime = new Date(getRelevantInvoiceTimestamp(left) || 0).getTime();
          const rightTime = new Date(getRelevantInvoiceTimestamp(right) || 0).getTime();
          return rightTime - leftTime;
        });
      setOrders(filtered);
    } catch {
      toast.error('Rechnungen konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const registerState = useMemo(() => getCashRegisterUiState(register), [register]);
  const activeShiftRange = useMemo(() => getCashRegisterTimeRange(register), [register]);

  const visibleOrders = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return orders
      .filter((order) => {
        if (statusFilter === 'completed') {
          return order.status !== 'cancelled' && isOrderPaid(order);
        }
        if (statusFilter === 'cancelled') {
          return order.status === 'cancelled';
        }
        return true;
      })
      .filter((order) => {
        if (!normalizedSearch) return true;
        const haystack = [
          getInvoiceNumber(order),
          getOrderDisplayName(order),
          order.cashier_name,
          order.table_name,
          order.customer_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(normalizedSearch);
      });
  }, [orders, search, statusFilter]);

  useEffect(() => {
    const nextSelection =
      visibleOrders.find((order) => order.id === selectedOrder?.id) || visibleOrders[0] || null;
    setSelectedOrder(nextSelection);
    setDetailOrder(nextSelection);
  }, [selectedOrder?.id, visibleOrders]);

  const summary = useMemo(() => {
    const completed = orders.filter((order) => order.status !== 'cancelled' && isOrderPaid(order));
    const cancelled = orders.filter((order) => order.status === 'cancelled');
    return {
      turnover: completed.reduce((sum, order) => sum + (order.total || 0), 0),
      completedCount: completed.length,
      cancelledCount: cancelled.length,
    };
  }, [orders]);

  const handleSelect = (order) => {
    setSelectedOrder(order);
    setDetailOrder(order);
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setShowMobileDetail(true);
    }
  };

  const handleReceipt = async (orderId, orderPreview = null) => {
    if (orderPreview) {
      setSelectedOrder(orderPreview);
      setDetailOrder(orderPreview);
    }
    try {
      const full = await getOrder(orderId);
      setDetailOrder(full);
      setShowReceipt(true);
    } catch {
      toast.error('Beleg konnte nicht geladen werden');
    }
  };

  const handleStornoInfo = () => {
    toast.info('Rechnungsstorno wird als eigener fiskaler Workflow ergänzt. Offene Bestellungen werden bereits separat storniert.');
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-muted lg:flex-row" data-testid="pos-invoices">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-card lg:border-r lg:border-border">
        <div className="border-b border-border bg-muted px-4 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="text-2xl font-bold tracking-tight text-foreground">Rechnungen</p>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                Nur Rechnungen aus dem aktuell offenen Kassentag.
                {activeShiftRange?.openedAt ? ` Seit ${formatFullDate(activeShiftRange.openedAt)}.` : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={() => detailOrder && handleReceipt(detailOrder.id, detailOrder)}
              disabled={!detailOrder}
              className="hidden h-11 items-center justify-center border border-border bg-card px-4 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 lg:inline-flex"
            >
              <Printer className="mr-2 h-4 w-4" />
              Ausgewählte drucken
            </button>
          </div>
        </div>

        <div className="border-b border-border px-4 py-4">
          <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_320px]">
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
              <SummaryCard
                label="Bezahlt"
                value={summary.completedCount}
                icon={CheckCircle2}
                tone="border-lime-200 bg-lime-50 text-lime-800"
              />
              <SummaryCard
                label="Storno"
                value={summary.cancelledCount}
                icon={XCircle}
                tone="border-red-200 bg-red-50 text-red-800"
              />
              <div className="col-span-2 lg:col-span-1">
                <SummaryCard
                  label="Umsatz"
                  value={formatCurrency(summary.turnover)}
                  icon={Receipt}
                  tone="border-border bg-card text-foreground"
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] xl:grid-cols-1">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Rechnung, Tisch oder Kunde suchen"
                  className="h-12 w-full border border-border bg-card pl-10 pr-4 text-sm text-muted-foreground outline-none transition-colors focus:border-primary"
                />
              </div>
              <button
                type="button"
                onClick={loadOrders}
                className="inline-flex h-12 items-center justify-center border border-border bg-card px-4 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted lg:hidden"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Aktualisieren
              </button>
            </div>
          </div>
        </div>

        <div className="border-b border-border px-4 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1">
              {FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setStatusFilter(filter.value)}
                  className={`whitespace-nowrap px-3 py-2 text-sm font-semibold transition-colors ${
                    statusFilter === filter.value
                      ? 'bg-primary text-primary-foreground'
                      : 'border border-border bg-card text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={loadOrders}
              className="hidden h-10 items-center justify-center border border-border bg-card px-3 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted lg:inline-flex"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </button>
          </div>
        </div>

        <div className="hidden border-b border-border bg-muted px-4 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground lg:grid lg:grid-cols-[88px_110px_120px_minmax(0,1fr)_180px_120px] lg:gap-4">
          <span>Nr.</span>
          <span>Datum</span>
          <span>Kellner</span>
          <span>Bestellung</span>
          <span className="text-right">Betrag / Zahlung</span>
          <span className="text-right">Status</span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto bg-muted/60 lg:bg-card">
          {isLoading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            </div>
          ) : !activeShiftRange ? (
            <div className="flex h-48 flex-col items-center justify-center px-6 text-center text-muted-foreground">
              <Receipt className="mb-3 h-12 w-12 opacity-30" />
              <p className="text-lg font-semibold">
                {registerState === 'closed' ? 'Tag beendet. Nach dem nächsten Öffnen startet die Rechnungsansicht leer.' : 'Kein offener Kassentag aktiv.'}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Auf dieser Seite zeigen wir nur Rechnungen innerhalb des aktuell geöffneten Tageszeitraums.
              </p>
            </div>
          ) : visibleOrders.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center px-6 text-center text-muted-foreground">
              <Receipt className="mb-3 h-12 w-12 opacity-30" />
              <p className="text-lg font-semibold">
                {orders.length === 0 ? 'Keine Rechnungen vorhanden' : 'Keine Rechnungen für diesen Filter'}
              </p>
            </div>
          ) : (
            visibleOrders.map((order) => (
              <InvoiceRow
                key={order.id}
                order={order}
                selected={selectedOrder?.id === order.id}
                onSelect={handleSelect}
                onReceipt={handleReceipt}
              />
            ))
          )}
        </div>
      </div>

      <div className="hidden w-[440px] flex-shrink-0 lg:block">
        <InvoiceDetail order={detailOrder} onReceipt={handleReceipt} onStornoInfo={handleStornoInfo} />
      </div>

      <ReceiptDialog
        open={showReceipt}
        onClose={() => setShowReceipt(false)}
        order={detailOrder}
        printTarget="invoice"
      />

      <Drawer open={showMobileDetail} onOpenChange={setShowMobileDetail}>
        {showMobileDetail ? (
          <DrawerContent className="flex h-[96vh] max-h-[96vh] min-h-0 flex-col overflow-hidden border-t border-border bg-card lg:hidden">
            <DrawerHeader className="sr-only">
              <DrawerTitle>Rechnungsdetails</DrawerTitle>
            </DrawerHeader>
            <InvoiceDetail
              order={detailOrder}
              onReceipt={handleReceipt}
              onStornoInfo={handleStornoInfo}
              isMobile
              onClose={() => setShowMobileDetail(false)}
            />
          </DrawerContent>
        ) : null}
      </Drawer>
    </div>
  );
}
